import { Container } from '@seek/ai-elements';
import { useTranslations } from '@vocab/react';
import { Box, Stack } from 'braid-design-system';
import { useMemo, type ComponentProps } from 'react';

import translations from './.vocab';
import {
  CareerFeedAIHeading,
  CareerFeedAIHeadingExperimental,
} from './CareerFeedAIHeading';
import { useCareerFeedAIAssistant } from './hooks/useCareerFeedAIAssistant';

import * as styles from './CareerFeedAIAssistantContainer.css';

const useWidgetHeading = () => {
  const { progress } = useCareerFeedAIAssistant();
  const { t } = useTranslations(translations);

  return useMemo(() => {
    switch (progress) {
      case 'initial':
      case 'gatheringRefinement':
      case 'collectGeneralRequirementsAtLast':
        return t('Want better job matches?');
      case 'recsRefined':
      case 'recsRefined-withPersistedData':
        return t('Refined for you');
    }
  }, [progress, t]);
};

export const CareerFeedAIAssistantWidget = ({
  showHeading,
  children,
}: {
  showHeading?: boolean;
  children: ComponentProps<typeof Stack>['children'];
}) => {
  const headingText = useWidgetHeading();

  return (
    <Box display="flex" flexDirection="column" width="full">
      <Container borderWidth="standard" padding="medium">
        <Stack space="medium">
          {showHeading && (
            <CareerFeedAIHeading headingText={headingText} adjacent />
          )}
          {children}
        </Stack>
      </Container>
    </Box>
  );
};

export const CareerFeedAIAssistantWidgetExperimental = ({
  showHeading,
  children,
}: {
  showHeading?: boolean;
  children: ComponentProps<typeof Stack>['children'];
}) => {
  const headingText = useWidgetHeading();

  return (
    <Box display="flex" flexDirection="column" width="full" paddingY="medium">
      <Stack space="xxsmall">
        {showHeading && (
          <CareerFeedAIHeadingExperimental headingText={headingText} adjacent />
        )}
        <Box className={styles.quoteBlock}>{children}</Box>
      </Stack>
    </Box>
  );
};
